// 86d3zp17w — cross-tab mutex for token refresh.
//
// The in-tab `refreshPromise` in apiClient coalesces concurrent refreshes within ONE tab. It cannot
// span tabs: a module-scoped variable is per-JS-context, and two tabs are two contexts. Tabs share one
// `localStorage`, so they hold the same refresh token and their access tokens expire in lockstep —
// which is how N tabs came to present the SAME single-use refresh token at the same instant, with the
// losers 401ing and tearing down a session that was alive.
//
// This is the browser equivalent of the mobile client's `SemaphoreSlim` in
// CSC_WMS_Mobile/Features/Authentication/Services/AuthenticatedHttpClientHandler.cs:104-149, which has
// never had this defect against the same rotating backend.
//
// Web Locks is Baseline (widely available since March 2022) but needs a SECURE CONTEXT, and this stack
// is genuinely reachable over plain HTTP (docker-compose.yml maps "8090:80"), so the lock-free path is
// real and must stay correct. It is: the server-side grace window accepts a token rotated seconds ago,
// so an uncoordinated duplicate refresh is answered 200 rather than 401.

const LOCK_NAME = 'csc_wms_token_refresh';

/**
 * How long to wait for a sibling tab's refresh before giving up and proceeding unlocked.
 * Must stay comfortably under the refresh POST's own timeout so a queued tab can still complete
 * its own request within the caller's patience.
 */
const LOCK_TIMEOUT_MS = 2000;

/**
 * Both capabilities are required, and they are checked together deliberately.
 *
 * `navigator.locks.request` without a signal waits FOREVER — there is no built-in timeout, and an
 * axios `timeout` does not bound a pre-dispatch interceptor await. A browser with Web Locks but
 * without `AbortSignal.timeout` would therefore hang every refresh instead of degrading, so we treat
 * that combination as "no locks" rather than risk it.
 */
export function hasWebLocks(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.locks?.request === 'function' &&
    typeof AbortSignal !== 'undefined' &&
    typeof AbortSignal.timeout === 'function'
  );
}

/**
 * Run `fn` while holding the cross-tab refresh lock.
 *
 * ⚠️ On lock-acquisition timeout this runs `fn` **unlocked** rather than failing. That is deliberate
 * and load-bearing — do not "tidy" it into an error path.
 *
 * A backgrounded tab can be frozen by the browser while holding a lock. If a timeout meant "give up",
 * every request in every tab would fail for as long as the frozen tab held it — a global stall this
 * app does not have today. Running unlocked instead degrades to today's behaviour (an uncoordinated
 * refresh), which the server's grace window already tolerates. Availability beats coordination here.
 *
 * The caller is still responsible for re-checking storage after acquisition — see the double-check in
 * `refreshAccessToken`. Serialising N refreshes is not the same as preventing N−1 of them.
 */
export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (!hasWebLocks()) return fn();

  try {
    return await navigator.locks.request(
      LOCK_NAME,
      { signal: AbortSignal.timeout(LOCK_TIMEOUT_MS) },
      () => fn()
    );
  } catch (error) {
    // Only an acquisition timeout falls back. Anything `fn` itself threw must propagate to the caller.
    const name = (error as Error | undefined)?.name;
    if (name === 'AbortError' || name === 'TimeoutError') {
      return fn();
    }
    throw error;
  }
}
